import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import {
  useBridgeApi,
  useCurrency,
  useDispatch,
  useGiftCards,
  useI18n,
  useSelector,
} from '~Hooks';
import {
  Button,
  Column,
  Columns,
  Card,
  MonoText,
  Badge,
  Input,
  Divider,
  Select,
  OutlinedButton,
  Box,
} from '~Styles';
import QRIcon from '~Assets/icons/qrcode-solid.svg';
import CloseIcon from '~Assets/icons/close.svg';
import {
  GetGiftCards,
  GetGiftCardsVariables,
  GetGiftCards_operativeViewer_giftcards_items as GiftCard,
} from '~Queries/__queries__';
import GiftCardHistory from '~Components/GiftCard/History';
import Icon from '~ComponentsImproved/Icon';
import NoContent from './NoContent';
import Loader from '~ComponentsImproved/Loader';
import Pagination from '~ComponentsImproved/Pagination';
import { addToast } from '~actions';
import CreateGiftCardForm from './CreateForm';
import EmailModal from './EmailModal';
import ThemedModal from './ThemedModal';
import FundsForm from './FundsForm';
import { GiftcardSearchFilter, GIFTCARD_STATUS } from '~../__queries__';
import LandingPage from './LandingPage';

const Sidebar = styled(Column)`
  height: calc(100vh - 64px);
`;

// Header with title and back button
const SideBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  height: 62px;
  margin-top: 12px;
  align-items: center;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  & > h3 {
    margin: 0;
  }
`;
const SideBarContent = styled.div`
  padding: 16px 24px;
  min-height: 200px;
  display: flex;
  align-self: stretch;
  flex-grow: 1;
  flex-direction: column;
`;

const BackButtonIcon = styled.i`
  font-size: 15px;
  font-weight: bold !important;
  color: #000;
`;

const CardListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  & > thead td {
    padding-bottom: 18px;
    font-size: 14px;
  }
  & tr > td:first-of-type {
    border-radius: 4px;
  }
  & tr > td:last-child {
    border-radius: 4px;
  }
`;

const CardHeading = styled.p`
  margin: 0;
  color: var(--colorBlueGray600);
  font-size: 14px;
`;

const CardRow = styled.tr<{ active: boolean }>`
  padding-bottom: 14px;
  background-color: ${({ active }) => active && '#f2f4f5'};
  & > td {
    position: relative;
  }
`;

const cardStatusColors: Record<GIFTCARD_STATUS, string> = {
  ACTIVE: 'colorStatusGreen',
  EXPIRED: 'colorStatusOrange',
  INACTIVE: 'colorStatusGray',
};

export default function GiftCards() {
  const dispatch = useDispatch();
  const bridgeApi = useBridgeApi();
  const { i18n } = useI18n();
  const { formatShortCode, print, fetchGiftCards } = useGiftCards();
  const hasPrimaryPrinter = useSelector((state) => state.printers.some((p) => p.primary));
  const [fetchingData, setFetchingData] = useState(true);
  const [giftCardData, setGiftCardData] = useState<GetGiftCards | null>(null);
  const giftCards = giftCardData?.operativeViewer?.giftcards?.items || [];
  const [selectedCard, setSelectedCard] = useState<GiftCard | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [predefinedShortcode, setPredefinedShortcode] = useState<string | undefined>();
  const [firstItemId, setFirstItemId] = useState<string | null>(null);

  const [fundsModalOpen, setFundsModalOpen] = useState(false);
  const [filterObject, setFilterObject] = useState<GiftcardSearchFilter>({ shortcode: '' });
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const currency = useCurrency();
  const { id: paramShortcode } = useParams<{ id: string }>();
  const history = useHistory();
  const companyId = useSelector((state) => state.company);

  const getGiftCards = (variables?: Partial<GetGiftCardsVariables>) => {
    setFetchingData(true);

    return fetchGiftCards(variables)
      .then((res) => {
        setGiftCardData(res.data);
        // Track first item of first page to determine if we need to reset pagination
        if (!variables?.after) {
          setFirstItemId(res.data?.operativeViewer?.giftcards?.items[0]?.id);
        }
      })
      .finally(() => setFetchingData(false));
  };

  const handleCardSelect = (shortcode: string | null) => {
    if (!shortcode) {
      // Remove id hash part of URL
      history.push(
        history.location.pathname
          .split('/')
          .slice(0, -1)
          .join('/'),
      );
      return;
    }

    if (paramShortcode) {
      history.push(shortcode);
    } else {
      history.push(`${history.location.pathname}/${shortcode}`);
    }
  };

  const handleFilterUpdate = (newFilter: GiftcardSearchFilter) => {
    setFilterObject((prev) => ({ ...prev, ...newFilter }));
    getGiftCards({ after: null, filter: newFilter });
  };

  // Get giftcard data on page load
  useEffect(() => {
    if (!companyId) {
      return;
    }
    getGiftCards();
  }, [companyId]);

  // Show selected card if it's in the URL
  useEffect(() => {
    if (fetchingData) {
      return;
    }

    setSelectedCard(giftCards.find((card) => card.shortcode === paramShortcode) || null);
  }, [paramShortcode, fetchingData]);

  // Select card if its the only one
  useEffect(() => {
    if (giftCards.length === 1) {
      handleCardSelect(giftCards[0].shortcode);
      return;
    }
  }, [giftCardData]);

  const printGiftCard = () => {
    // Check if primary printer exists
    if (!hasPrimaryPrinter) {
      dispatch(addToast('No primary printer found', true, undefined, true));
      return;
    }

    if (selectedCard) {
      print(selectedCard);
    }
  };

  const initiateScanning = async () => {
    const scannedCode = await bridgeApi.scanQRCode();
    if (scannedCode) {
      handleFilterUpdate({ shortcode: scannedCode.toUpperCase() });
    } else {
      dispatch(addToast('No QR code detected', true, undefined, true));
    }
  };

  const scanExistingCard = async () => {
    let scannedCode = await bridgeApi.scanQRCode();
    if (scannedCode) {
      // Upper case the scanned code
      scannedCode = scannedCode.toUpperCase();

      // Check if there is a gift card with this shortcode
      const { data } = await fetchGiftCards({ filter: { shortcode: scannedCode } });
      const giftCard = data?.operativeViewer?.giftcards?.items[0];
      if (giftCard) {
        handleCardSelect(scannedCode);
        setSelectedCard(giftCard);
        // Open add balance modal
        setFundsModalOpen(true);
      } else {
        setPredefinedShortcode(scannedCode);
        setCreateModalOpen(true);
      }
    } else {
      dispatch(addToast('No QR code detected', true, undefined, true));
    }
  };

  // Triggered on successfull GC purchase/balance increase through modal
  const handlePurchaseComplete = (shortcode: string | undefined) => {
    setCreateModalOpen(false);
    setFundsModalOpen(false);
    setPredefinedShortcode(undefined);
    // Toast success
    dispatch(addToast(i18n('SSK.Checkout.Status.COMPLETED'), true));

    // Refetch the gift cards, and select the created/modified one
    // "after: null" to make it go to page 1 again and reset pagination
    getGiftCards({ after: null }).then(() => {
      shortcode && handleCardSelect(shortcode);
    });
  };

  return (
    <>
      <Columns>
        <Sidebar width={4} boxShadow="medium" backgroundColor="colorWhite">
          {/* Create Modal */}
          <ThemedModal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
            <CreateGiftCardForm
              onSuccess={handlePurchaseComplete}
              predefinedShortcode={predefinedShortcode}
            />
          </ThemedModal>
          {/* Add funds Modal */}
          <ThemedModal width="40vw" open={fundsModalOpen} onClose={() => setFundsModalOpen(false)}>
            <FundsForm giftCard={selectedCard} onSuccess={handlePurchaseComplete} />
          </ThemedModal>
          <SideBarHeader>
            <OutlinedButton>
              <BackButtonIcon className={'icon arrow_left'} />
            </OutlinedButton>
            <h3>{i18n('GiftCards')}</h3>
            <Button backgroundColor="--colorWhite" onClick={() => setCreateModalOpen(true)}>
              {i18n('GC.Button.New')}
            </Button>
          </SideBarHeader>
          <Divider marginBottom="small" />
          <SideBarContent>
            <Columns marginBottom="small">
              <Column width={10}>
                <Input
                  fluid
                  uppercase
                  name="shortcode"
                  placeholder={i18n('GC.Search.Placeholder')}
                  type="text"
                  value={filterObject.shortcode}
                  onChange={(e) => {
                    const normalized = (e.target as HTMLInputElement).value
                      .replace(/-/g, '')
                      .toUpperCase();
                    handleFilterUpdate({
                      shortcode: normalized,
                    });
                  }}
                />
              </Column>
              <Column
                alignItems="center"
                justifyContent="center"
                onTouchStart={() => initiateScanning()}>
                <Icon src={QRIcon} color={'var(--colorUIBlue)'} />
              </Column>
            </Columns>
            <Select
              name="status"
              placeholder="Status"
              marginBottom="large"
              value={filterObject.status}
              onChange={(e) => {
                handleFilterUpdate({
                  status:
                    (e.target as HTMLInputElement).value === 'ANY'
                      ? undefined
                      : ((e.target as HTMLInputElement).value as GIFTCARD_STATUS),
                });
              }}>
              <option value="ANY">Any</option>
              {Object.values(GIFTCARD_STATUS).map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
            <div style={{ position: 'relative', height: '100%', overflow: 'scroll' }}>
              {fetchingData && <Loader />}
              {!fetchingData && !giftCards?.length && <NoContent refetch={getGiftCards} />}
              {!!giftCards.length && (
                <CardListTable>
                  <thead>
                    <tr style={{ color: 'var(--colorBlueGray600)' }}>
                      <td>{i18n('GC.Code')}</td>
                      <td>{i18n('GC.Status')}</td>
                      <td>{i18n('GC.Balance')}</td>
                    </tr>
                  </thead>
                  <tbody>
                    {giftCards.map((card) => (
                      <CardRow
                        key={card.id}
                        onClick={() => handleCardSelect(card.shortcode)}
                        active={paramShortcode === card.shortcode}>
                        <td>
                          <MonoText>{formatShortCode(card.shortcode)}</MonoText>
                        </td>
                        <td>
                          <Badge backgroundColor={cardStatusColors[card.status]}>
                            {card.status}
                          </Badge>
                        </td>
                        <td>
                          <b>{currency.format(card.balance)}</b>
                        </td>
                      </CardRow>
                    ))}
                  </tbody>
                </CardListTable>
              )}
            </div>
            <Pagination
              key={`${JSON.stringify(filterObject)}${firstItemId}`} // to reset pagination when filters change
              lastKey={giftCardData?.operativeViewer?.giftcards?.lastKey}
              onUpdate={(newAfter) => getGiftCards({ filter: filterObject, after: newAfter })}
              loading={fetchingData}
            />
          </SideBarContent>
        </Sidebar>
        {/* Main content */}
        {!selectedCard && (
          <LandingPage
            setCreateModalOpen={setCreateModalOpen}
            scanExistingCard={scanExistingCard}
          />
        )}
        {selectedCard && (
          <Column padding="xl" style={{ overflowY: 'scroll', height: 'calc(100vh - 64px)' }}>
            {/* Close button */}
            <Box
              fluid
              marginBottom="small"
              display="flex"
              flexDirection="row"
              justifyContent="flex-end">
              <div
                onTouchStart={() => {
                  handleCardSelect(null);
                  setPredefinedShortcode(undefined);
                }}>
                <Icon src={CloseIcon} color="#333" />
              </div>
            </Box>
            <EmailModal
              open={emailModalOpen}
              onClose={() => setEmailModalOpen(false)}
              shortcode={selectedCard.shortcode}
              balance={selectedCard.balance}
            />
            <MonoText marginBottom="small" textAlign="center" fluid>
              {formatShortCode(selectedCard.shortcode)}
            </MonoText>
            <Card fluid marginBottom="medium" boxShadow="medium" textAlign="center">
              <CardHeading>{i18n('GC.Balance')}</CardHeading>
              <b style={{ fontWeight: 'bold', fontSize: '80px' }}>
                {currency.format(selectedCard.balance)}
              </b>
              <Divider marginBottom="small" />
              <p style={{ margin: 0 }}>
                {i18n('GiftCard.ExpiresAt')}:{' '}
                {moment(selectedCard.expiresAt).format('D MMMM YYYY')}
              </p>
            </Card>
            <Box
              fluid
              marginBottom="small"
              display="flex"
              flexDirection="row"
              justifyContent="center">
              <Button
                backgroundColor="colorBlack"
                color="colorWhite"
                marginRight="small"
                onTouchStart={printGiftCard}>
                {i18n('GC.Button.Print')}
              </Button>
              <Button
                onClick={() => setEmailModalOpen(true)}
                backgroundColor="colorBlack"
                color="colorWhite"
                marginRight="small">
                {i18n('GC.Button.Email')}
              </Button>
              <Button
                onClick={() => setFundsModalOpen(true)}
                backgroundColor="colorBlack"
                color="colorWhite"
                marginRight="small">
                {i18n('GC.Button.AddFunds')}
              </Button>
            </Box>
            <Card boxShadow="medium" fluid>
              <CardHeading>{i18n('GC.History')}</CardHeading>
              <GiftCardHistory historyEvents={selectedCard.history || []} />
            </Card>
          </Column>
        )}
      </Columns>
    </>
  );
}
